
class ProductStickyCTA extends HTMLElement {
    private footerEl: Element | null;
    private readonly focusableSelector = 'a, button, input, select, textarea, cta-cart, [tabindex]:not([tabindex="-1"])';
    private readonly isComingSoon: boolean;

    constructor() {
      super();
      this.footerEl = document.querySelector('.footer');
      this.isComingSoon = !!document.querySelector('main-product[data-coming-soon]');
      this.bindEvents();
      this.setActiveState(false);
    }

    bindEvents() { 
       if (!this.isComingSoon) {
         const ctaButtonElement = this.querySelector("cta-cart");
         ctaButtonElement?.addEventListener("click", this.onCTAClick.bind(this));
       }

       window.addEventListener('scroll', this.enableStickyCTA.bind(this));

       // Re-run the visibility check once focus leaves the bar -- SG 20261026
       this.addEventListener('focusout', (event) => {
           if (this.contains(event.relatedTarget as Node | null)) return;
           requestAnimationFrame(() => this.enableStickyCTA());
       });
    }

    onCTAClick() {
        if (!this.classList.contains('active')) return;

        const trueCTAButton = document.querySelector(".product__information cta-cart") as HTMLElement;
        trueCTAButton?.click();
    }

    enableStickyCTA(event?: Event) {
        const ctaElement = document.querySelector(".product__information .product__cta-group");
        const offset = ctaElement?.getBoundingClientRect().top;

        // Hide sticky bar when footer content is visible beneath it -- SG 20260506
        const footerTop = this.footerEl?.getBoundingClientRect().top;
        const footerReached = footerTop != null && footerTop <= window.innerHeight - 16;
        const shouldShow = !!(offset && offset < 90 && !footerReached);

        // Keep the bar shown while it holds focus and the main CTA is out of view, so focus never lands off screen -- SG 20261026
        if (!shouldShow && this.classList.contains('active') && this.hasVisibleFocusInside() && !this.isInView(this.mainCta())) return;

        this.setActiveState(shouldShow);
    }

    // Keyboard-style focus only (:focus-visible), so a mouse click on the sticky controls cannot pin the bar over the footer -- EZ 20261028
    private hasVisibleFocusInside(): boolean {
        try {
            return !!this.querySelector(":focus-visible");
        } catch {
            return this.contains(document.activeElement);
        }
    }

    private mainCta(): HTMLElement | null {
        return this.isComingSoon
          ? document.querySelector<HTMLElement>(".product__information .product__cart-cta")
          : document.querySelector<HTMLElement>(".product__information cta-cart");
    }

    // 90 matches the header band in enableStickyCTA. A shown bar is fixed at the bottom, so its top is the visible limit -- SG 20261026
    private isInView(element: HTMLElement | null): boolean {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        const bottomLimit = this.classList.contains('active') ? this.getBoundingClientRect().top : window.innerHeight;
        return rect.width > 0 && rect.top >= 90 && rect.bottom <= bottomLimit;
    }

    setActiveState(isActive: boolean) {
        this.classList.toggle('active', isActive);

        if (isActive) {
            this.removeAttribute('aria-hidden');
            this.removeAttribute('inert');
        }
        else {
            // Read focus before inert, because inert moves focus out first. Only move focus to a CTA the shopper can see -- SG 20261026
            const hadFocus = this.contains(document.activeElement);
            this.setAttribute('aria-hidden', 'true');
            this.setAttribute('inert', '');
            if (hadFocus) {
                const focusTarget = this.mainCta();
                if (this.isInView(focusTarget)) {
                    focusTarget?.focus?.({ preventScroll: true });
                }
            }
        }

        this.querySelectorAll<HTMLElement>(this.focusableSelector).forEach((element) => {
            if (isActive) {
                const previousTabIndex = element.dataset.stickyCtaTabindex;
                if (previousTabIndex == null) return;

                if (previousTabIndex === '') {
                    element.removeAttribute('tabindex');
                }
                else {
                    element.setAttribute('tabindex', previousTabIndex);
                }
                delete element.dataset.stickyCtaTabindex;
                return;
            }

            if (element.dataset.stickyCtaTabindex == null) {
                element.dataset.stickyCtaTabindex = element.getAttribute('tabindex') ?? '';
            }
            element.setAttribute('tabindex', '-1');
        });
    }
  
}
  
customElements.define('product-sticky-cta', ProductStickyCTA);